import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { AuthConfig } from '../types/api/AuthConfig';
import { AuthState } from '../types/api/AuthState';
import { apiService } from '../services/api';
import {
  SchoolMealFormData,
  DietaryRestrictions,
  GetFormDataRequest,
  FormTexts,
  StudentInfo,
} from '../types/form';
import type { GetConsentDataResponse } from '../types/consent';
import { ApiError } from '../types/api/ApiError';
import { ERROR_CODE_FORBIDDEN } from '../utils/constants';
import { consumeOAuthReturnTo } from '../utils/oauthReturnPath';

const isDevelopment = process.env.NODE_ENV === 'development';

const getMockFormData = (studentId?: string): SchoolMealFormData => ({
  studentInfo: {
    studentId: studentId || 'DEV-12345',
    studentFirstName: 'John',
    studentLastName: 'Doe',
    parentName: 'Jane Doe',
    studentGrade: '5',
  },
  mealPreferences: {
    breakfast: true,
    lunch: true,
    afternoonSnack: false,
  },
  dietaryRestrictions: {
    selected: ['Vegetarian', 'Gluten Free'],
  },
});

const getMockTexts = (): FormTexts => ({
  'General info text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;"><span style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);display&#58;inline !important;">The answers for the next month are locked in at the 25th. For extra information click<span>&#160;</span></span><a href="https&#58;//ist.ee/" title="here" style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);">here</a><span style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);display&#58;inline !important;">&#160;</span><br></div>',
  'Breakfast text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Breakfast is available from 08&#58;30 until 9&#58;30</div>',
  'Lunch text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Lunch is available at 11&#58;30 until 12&#58;30</div>',
  'Afternoon snack text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Afternoon snack is available after 14&#58;00 until closing</div>',
});

/** Meal form shell when only consent data has been loaded (user can open meals tab next). */
function mealFormPlaceholderFromStudentInfo(info: StudentInfo): SchoolMealFormData {
  return {
    studentInfo: info,
    mealPreferences: { breakfast: false, lunch: false, afternoonSnack: false },
    dietaryRestrictions: { selected: [] },
  };
}

type SessionGate = {
  studentId: string;
  mealBundleLoaded: boolean;
  /** Full consent payload has been loaded for this student this session */
  consentsBundleLoaded: boolean;
};

interface AuthContextType extends AuthState {
  login: () => void;
  logout: () => void;
  initialize: (config: AuthConfig) => void;
  /** Re-fetch consent data from the server (e.g. after 409 conflict). */
  refreshConsentData: () => Promise<void>;
  /** Replace session consent payload (e.g. optimistic updates after save). */
  replaceConsentData: (data: GetConsentDataResponse) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  config: AuthConfig;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, config }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchString = searchParams.toString();

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    tokenExpiry: null,
    isLoading: true,
    isMealDataLoading: false,
    error: null,
    formData: null,
    texts: null,
    consentData: null,
    consentLoadError: null,
    isConsentDetailLoading: false,
  });

  const sessionGateRef = useRef<SessionGate | null>(null);
  /** Bumped on each consent fetch start; completions only apply state if still the latest (avoids stale updates when consent load or refresh runs more than once close together). */
  const consentDataFetchGenerationRef = useRef(0);
  /** Skips starting another getConsentData for the same student while one is still in flight (e.g. meals prefetch not finished when opening Consents). */
  const consentDetailInflightStudentRef = useRef<string | null>(null);
  /** Skips starting another getFormData for the same student while skip-global meal load is in flight (consents-tab prefetch or rapid tab switches). */
  const mealDataInflightStudentRef = useRef<string | null>(null);

  const loadConsentDetail = useCallback(async (studentId: string) => {
    const gateBefore = sessionGateRef.current;
    if (gateBefore?.studentId !== studentId) return;

    if (consentDetailInflightStudentRef.current === studentId) {
      return;
    }
    consentDetailInflightStudentRef.current = studentId;

    const generation = ++consentDataFetchGenerationRef.current;

    setAuthState((prev) => ({
      ...prev,
      isConsentDetailLoading: true,
      consentLoadError: null,
    }));

    try {
      const res = await apiService.getConsentData({ studentId });
      if (sessionGateRef.current?.studentId !== studentId) return;
      if (generation !== consentDataFetchGenerationRef.current) return;

      const g = sessionGateRef.current;
      sessionGateRef.current = {
        studentId,
        mealBundleLoaded: g?.mealBundleLoaded ?? false,
        consentsBundleLoaded: true,
      };
      setAuthState((prev) => ({
        ...prev,
        consentData: res,
        isConsentDetailLoading: false,
        consentLoadError: null,
      }));
    } catch (error) {
      if (sessionGateRef.current?.studentId !== studentId) return;
      if (generation !== consentDataFetchGenerationRef.current) return;

      const isForbidden =
        error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 403;

      if (isForbidden) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          isConsentDetailLoading: false,
          error: ERROR_CODE_FORBIDDEN,
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
        }));
        return;
      }

      const msg = error instanceof Error ? error.message : 'Failed to load consents';
      setAuthState((prev) => ({
        ...prev,
        isConsentDetailLoading: false,
        consentLoadError: msg,
      }));
    } finally {
      if (consentDetailInflightStudentRef.current === studentId) {
        consentDetailInflightStudentRef.current = null;
      }
    }
  }, []);

  const loadInitialData = useCallback(
    async (
      studentId: string,
      authToken?: string | null,
      options?: { skipGlobalLoading?: boolean }
    ) => {
    const skipGlobalLoading = !!options?.skipGlobalLoading;

    if (skipGlobalLoading && mealDataInflightStudentRef.current === studentId) {
      return;
    }
    if (skipGlobalLoading) {
      mealDataInflightStudentRef.current = studentId;
    }

    if (!skipGlobalLoading) {
      setAuthState((prev: AuthState) => ({
        ...prev,
        isLoading: true,
        isMealDataLoading: false,
        error: null,
        consentData: null,
        consentLoadError: null,
        isConsentDetailLoading: false,
      }));
    } else {
      setAuthState((prev: AuthState) => ({
        ...prev,
        error: null,
        isMealDataLoading: true,
      }));
    }

    try {
    if (isDevelopment) {
      await new Promise(resolve => setTimeout(resolve, 500));
      const mockFormData = getMockFormData(studentId);
      const mockTexts = getMockTexts();
      const prevG = sessionGateRef.current;
      const consentsBundleLoaded =
        prevG?.studentId === studentId ? !!prevG.consentsBundleLoaded : false;
      sessionGateRef.current = { studentId, mealBundleLoaded: true, consentsBundleLoaded };
      setAuthState((prev: AuthState) => ({
        isAuthenticated: true,
        accessToken: null,
        tokenExpiry: null,
        isLoading: false,
        isMealDataLoading: false,
        error: null,
        formData: mockFormData,
        texts: mockTexts,
        consentData: consentsBundleLoaded ? prev.consentData : null,
        consentLoadError: null,
        isConsentDetailLoading: false,
      }));
      if (!consentsBundleLoaded) {
        void loadConsentDetail(studentId);
      }
      return;
    }

    try {
      const payload: GetFormDataRequest = authToken
        ? { studentId, authToken }
        : { studentId };

      const formDataResponse = await apiService.getFormData(payload);

      let normalizedFormData: SchoolMealFormData | null = null;
      const rawFormData = formDataResponse?.formResponse?.formData;
      const texts = formDataResponse?.formResponse?.texts ?? null;

      if (rawFormData) {
        const rawDietary = (rawFormData as any).dietaryRestrictions;
        let dietaryRestrictions: DietaryRestrictions;

        if (Array.isArray(rawDietary)) {
          dietaryRestrictions = { selected: rawDietary };
        } else if (rawDietary && typeof rawDietary === 'object') {
          const selected = rawDietary.selected ?? [];
          const custom = rawDietary.custom ?? [];
          dietaryRestrictions = { selected: [...selected, ...custom] };
        } else {
          dietaryRestrictions = { selected: [] };
        }

        normalizedFormData = {
          studentInfo: rawFormData.studentInfo,
          mealPreferences: rawFormData.mealPreferences,
          dietaryRestrictions,
        };
      }

      const prevG = sessionGateRef.current;
      const consentsBundleLoaded =
        prevG?.studentId === studentId ? !!prevG.consentsBundleLoaded : false;
      sessionGateRef.current = { studentId, mealBundleLoaded: true, consentsBundleLoaded };
      setAuthState((prev: AuthState) => ({
        isAuthenticated: true,
        accessToken: null,
        tokenExpiry: null,
        isLoading: false,
        isMealDataLoading: false,
        error: null,
        formData: normalizedFormData,
        texts,
        consentData: consentsBundleLoaded ? prev.consentData : null,
        consentLoadError: null,
        isConsentDetailLoading: false,
      }));
      if (!consentsBundleLoaded) {
        void loadConsentDetail(studentId);
      }
    } catch (error) {
      const isForbidden = error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 403;

      if (isForbidden) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: ERROR_CODE_FORBIDDEN,
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        }));
        return;
      }

      if (authToken) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: error instanceof Error
            ? error.message
            : 'Failed to authenticate with OAuth provider. Please try again.',
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        }));
        return;
      }

      try {
        authService.login();
        return;
      } catch (loginError) {
        setAuthState((prev: AuthState) => ({
          ...prev,
          isLoading: false,
          isMealDataLoading: false,
          error:
            loginError instanceof Error
              ? loginError.message
              : 'Failed to initiate authentication',
        }));
        return;
      }
    }
    } finally {
      if (skipGlobalLoading) {
        setAuthState((prev) => ({ ...prev, isMealDataLoading: false }));
        if (mealDataInflightStudentRef.current === studentId) {
          mealDataInflightStudentRef.current = null;
        }
      }
    }
  }, [loadConsentDetail]);

  const loadConsentBootstrap = useCallback(async (studentId: string, authToken?: string | null) => {
    setAuthState((prev: AuthState) => ({
      ...prev,
      isLoading: true,
      error: null,
      consentData: null,
      consentLoadError: null,
      isConsentDetailLoading: false,
    }));

    if (isDevelopment) {
      const res = await apiService.getConsentData(
        authToken ? { studentId, authToken } : { studentId }
      );
      const info: StudentInfo =
        res.studentInfo ?? {
          studentId,
          studentFirstName: '',
          studentLastName: '',
          parentName: '',
          studentGrade: '',
        };
      sessionGateRef.current = { studentId, mealBundleLoaded: false, consentsBundleLoaded: true };
      setAuthState({
        isAuthenticated: true,
        accessToken: null,
        tokenExpiry: null,
        isLoading: false,
        isMealDataLoading: false,
        error: null,
        formData: mealFormPlaceholderFromStudentInfo(info),
        texts: null,
        consentData: res,
        consentLoadError: null,
        isConsentDetailLoading: false,
      });
      void loadInitialData(studentId, undefined, { skipGlobalLoading: true });
      return;
    }

    try {
      const res = await apiService.getConsentData(
        authToken ? { studentId, authToken } : { studentId }
      );
      const info: StudentInfo =
        res.studentInfo ?? {
          studentId,
          studentFirstName: '',
          studentLastName: '',
          parentName: '',
          studentGrade: '',
        };
      sessionGateRef.current = { studentId, mealBundleLoaded: false, consentsBundleLoaded: true };
      setAuthState({
        isAuthenticated: true,
        accessToken: null,
        tokenExpiry: null,
        isLoading: false,
        isMealDataLoading: false,
        error: null,
        formData: mealFormPlaceholderFromStudentInfo(info),
        texts: null,
        consentData: res,
        consentLoadError: null,
        isConsentDetailLoading: false,
      });
      void loadInitialData(studentId, undefined, { skipGlobalLoading: true });
    } catch (error) {
      const isForbidden = error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 403;

      if (isForbidden) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: ERROR_CODE_FORBIDDEN,
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        }));
        return;
      }

      if (authToken) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: error instanceof Error
            ? error.message
            : 'Failed to authenticate with OAuth provider. Please try again.',
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        }));
        return;
      }

      try {
        authService.login();
        return;
      } catch (loginError) {
        setAuthState((prev: AuthState) => ({
          ...prev,
          isLoading: false,
          isMealDataLoading: false,
          error:
            loginError instanceof Error
              ? loginError.message
              : 'Failed to initiate authentication',
        }));
        return;
      }
    }
  }, [loadInitialData]);

  useEffect(() => {
    authService.initialize(config);

    if (typeof window === 'undefined') {
      return;
    }

    const pathname = location.pathname;
    const params = new URLSearchParams(searchString);
    let studentId = params.get('studentId')?.trim() ?? '';

    const isAppRoute = pathname === '/' || pathname === '';
    const isConsentsRoute = pathname === '/consents';
    const isCallbackRoute = pathname === '/auth/callback' || pathname.startsWith('/auth/callback');

    if (isCallbackRoute) {
      if (isDevelopment) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isLoading: false,
          isMealDataLoading: false,
          isAuthenticated: false,
          error: 'OAuth callback is not used in development mode.',
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        }));
        return;
      }

      const authToken = params.get('authCode') || params.get('code');
      const state = params.get('state');

      if (!authToken || !state) {
        sessionGateRef.current = null;
        setAuthState({
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: 'Invalid authentication callback parameters.',
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        });
        return;
      }

      (async () => {
        await loadInitialData(state, authToken);
        const target = consumeOAuthReturnTo(state);
        navigate(target, { replace: true });
      })();
      return;
    }

    if (isAppRoute || isConsentsRoute) {
      if (isDevelopment && isAppRoute && !studentId) {
        studentId = 'DEV-12345';
      }

      if (!studentId) {
        sessionGateRef.current = null;
        setAuthState({
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          error: 'Student ID is missing from URL.',
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
          isConsentDetailLoading: false,
        });
        return;
      }

      const gate = sessionGateRef.current;
      if (gate?.studentId === studentId) {
        if (isAppRoute && !gate.mealBundleLoaded) {
          loadInitialData(studentId, undefined, { skipGlobalLoading: true });
        } else if (isAppRoute && gate.mealBundleLoaded && !gate.consentsBundleLoaded) {
          loadConsentDetail(studentId);
        } else if (isConsentsRoute && !gate.consentsBundleLoaded) {
          loadConsentDetail(studentId);
        } else if (isConsentsRoute && gate.consentsBundleLoaded && !gate.mealBundleLoaded) {
          loadInitialData(studentId, undefined, { skipGlobalLoading: true });
        } else {
          setAuthState((prev: AuthState) =>
            prev.isLoading
              ? { ...prev, isLoading: false, isMealDataLoading: false }
              : prev
          );
        }
        return;
      }

      if (isConsentsRoute) {
        loadConsentBootstrap(studentId);
      } else {
        loadInitialData(studentId);
      }
      return;
    }

    sessionGateRef.current = null;
    setAuthState((prev: AuthState) => ({
      ...prev,
      isAuthenticated: false,
      isLoading: false,
      isMealDataLoading: false,
      error: 'Unsupported route. Please use the form link containing a student ID.',
      consentData: null,
      consentLoadError: null,
      isConsentDetailLoading: false,
    }));
  }, [config, loadInitialData, loadConsentBootstrap, loadConsentDetail, location.pathname, searchString, navigate]);

  const refreshConsentData = useCallback(async () => {
    const sid = sessionGateRef.current?.studentId;
    if (!sid) return;

    const generation = ++consentDataFetchGenerationRef.current;

    setAuthState((prev) => ({
      ...prev,
      isConsentDetailLoading: true,
      consentLoadError: null,
    }));

    try {
      const res = await apiService.getConsentData({ studentId: sid });
      if (sessionGateRef.current?.studentId !== sid) return;
      if (generation !== consentDataFetchGenerationRef.current) return;

      setAuthState((prev) => ({
        ...prev,
        consentData: res,
        isConsentDetailLoading: false,
        consentLoadError: null,
      }));
    } catch (error) {
      if (sessionGateRef.current?.studentId !== sid) return;
      if (generation !== consentDataFetchGenerationRef.current) return;

      const isForbidden =
        error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 403;

      if (isForbidden) {
        sessionGateRef.current = null;
        setAuthState((prev: AuthState) => ({
          ...prev,
          isAuthenticated: false,
          accessToken: null,
          tokenExpiry: null,
          isLoading: false,
          isMealDataLoading: false,
          isConsentDetailLoading: false,
          error: ERROR_CODE_FORBIDDEN,
          formData: null,
          texts: null,
          consentData: null,
          consentLoadError: null,
        }));
        return;
      }

      const msg = error instanceof Error ? error.message : 'Failed to load consents';
      setAuthState((prev) => ({
        ...prev,
        isConsentDetailLoading: false,
        consentLoadError: msg,
      }));
    }
  }, []);

  const replaceConsentData = useCallback((data: GetConsentDataResponse) => {
    setAuthState((prev) => ({ ...prev, consentData: data }));
  }, []);

  const login = () => {
    if (isDevelopment) {
      const params = new URLSearchParams(window.location.search);
      const studentId = params.get('studentId') || 'DEV-12345';
      loadInitialData(studentId);
      return;
    }

    try {
      authService.login();
    } catch (error) {
      setAuthState((prev: AuthState) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
    }
  };

  const logout = () => {
    authService.logout();
    sessionGateRef.current = null;
    mealDataInflightStudentRef.current = null;
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      tokenExpiry: null,
      isLoading: false,
      isMealDataLoading: false,
      error: null,
      formData: null,
      texts: null,
      consentData: null,
      consentLoadError: null,
      isConsentDetailLoading: false,
    });
  };

  const initialize = (newConfig: AuthConfig) => {
    authService.initialize(newConfig);
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    initialize,
    refreshConsentData,
    replaceConsentData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
